import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import snakeCase from 'lodash/snakeCase';
import { selectActiveCategory, selectFavoritesAsCategory } from './redux/selectors';
import VideoTile from './common/VideoTile';
import Immutable from 'immutable';
import Icon from '../../common/icons/Icon';
import InputScaffold from '../../common/forms/InputScaffold';
import { sendIntercomEvent } from '../../common/events';

import {
  favoriteLibraryVideo,
  unfavoriteLibraryVideo,
  favoriteEpisodeVideo,
  unfavoriteEpisodeVideo,
} from './favorites/redux/actions';

const DEFAULT_VIDEO_DISPLAY_COUNT = 10;

class CategoryDetailPage extends Component {
  static propTypes = {
    activeCategory: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    let sectionViewCounts = {};

    const sections = props.activeCategory.get('sections');

    if (sections) {
      sectionViewCounts = sections.reduce((accumulator, section) => {
        accumulator[section.get('id')] = DEFAULT_VIDEO_DISPLAY_COUNT;
        return accumulator;
      }, {});
    }

    this.state = {
      sectionViewCounts,
      searchQuery: '',
    };
  }

  componentDidMount = () => {
    const { activeCategory } = this.props;
    sendIntercomEvent(`viewed_${snakeCase(activeCategory.get('name'))}`);
  };

  handleFavoriteToggle = (e, video) => {
    e.preventDefault();
    let action;
    if (video.get('show_id')) {
      action = video.get('favorited') ? unfavoriteEpisodeVideo : favoriteEpisodeVideo;
    } else {
      action = video.get('favorited') ? unfavoriteLibraryVideo : favoriteLibraryVideo;
    }

    this.props.dispatch(action(video.get('id')));
  };

  handleSearchQueryChange = e => {
    const searchQuery = e.target.value;
    this.setState(state => ({ searchQuery }));
  };

  handleMoreClick(sectionId) {
    const { sectionViewCounts } = this.state;
    const currentViewCount = sectionViewCounts[sectionId];
    const newViewCounts = Object.assign({}, sectionViewCounts, {
      [sectionId]: currentViewCount + DEFAULT_VIDEO_DISPLAY_COUNT,
    });

    this.setState(state => ({ sectionViewCounts: newViewCounts }));
  }

  // Have to account for the height of the navbar
  // when scrolling down to section
  handleScrollToChange = e => {
    const sectionId = e.target.value;
    const desiredSection = this.refs[`section-${sectionId}`].getBoundingClientRect();
    window.scrollTo(0, desiredSection.top - 72);
  };

  renderSearchResults() {
    const { activeCategory } = this.props;
    const { searchQuery } = this.state;

    const loweredSearchQuery = searchQuery.toLowerCase();

    const relevantRecords = activeCategory.get('sections').reduce((accumulator, section) => {
      return accumulator.concat(
        section.get('libraryRecords').reduce((relevantRecords, libraryRecord) => {
          if (
            (libraryRecord.get('name') || '').toLowerCase().indexOf(loweredSearchQuery) !== -1 ||
            (libraryRecord.get('description') || '').toLowerCase().indexOf(loweredSearchQuery) !== -1
          ) {
            return relevantRecords.push(libraryRecord);
          }
          return relevantRecords;
        }, new Immutable.List())
      );
    }, new Immutable.List());

    if (!relevantRecords.size) {
      return <div> No videos match the search query </div>;
    }

    return <div className="lib-cat--section">{this.renderVideoList(relevantRecords)}</div>;
  }

  renderSection(section) {
    const maxDisplayCount = this.state.sectionViewCounts[section.get('id')];
    const videoListChunk = section.get('libraryRecords').slice(0, maxDisplayCount);

    const moreButton =
      section.get('libraryRecords').size > videoListChunk.size ? (
        <button type="submit" className="button" onClick={() => this.handleMoreClick(section.get('id'))}>
          Show More
        </button>
      ) : null;
    const renderedVideoList = this.renderVideoList(section.get('libraryRecords'), maxDisplayCount);

    return (
      <div className="lib-cat--section" key={section.get('id')} ref={`section-${section.get('id')}`}>
        <h3>{section.get('name')}</h3>
        {renderedVideoList}
        <div className="lib--show-more">{moreButton}</div>
      </div>
    );
  }

  renderVideoList(videos, count) {
    const { categoryId } = this.props.match.params;

    if (!videos.size) {
      return <div>No Videos</div>;
    }

    const videoGroup = count ? videos.slice(0, count) : videos;
    const renderedVideos = videoGroup.map(video => {
      const locked =
        this.props.match.params.categoryId === 'favorites'
          ? false
          : !video.get('user_eligible') && !video.get('show_id');
      const videoLocation = video.get('show_id')
        ? { pathname: `/subscription/tv/${video.get('show_id')}/${video.get('id')}`, state: { fromFavorites: true } }
        : `/subscription/library/${categoryId}/videos/${video.get('id')}`;
      return (
        <VideoTile
          locked={locked}
          key={video.get('id')}
          video={video}
          onFavoriteToggle={this.handleFavoriteToggle}
          videoLocation={videoLocation}
        />
      );
    });

    return <div className="lib-cat--video-list">{renderedVideos}</div>;
  }

  render() {
    const { activeCategory } = this.props;

    const pageContent = this.state.searchQuery.length
      ? this.renderSearchResults()
      : activeCategory.get('sections').map(section => this.renderSection(section));

    const sectionScrollToOptions = activeCategory.get('sections').map(section => {
      return (
        <option key={section.get('id')} value={section.get('id')}>
          {section.get('name')}
        </option>
      );
    });

    return (
      <div className="lib-wrapper lib-cat__wrapper">
        <Link className="ms__back-to" to="/subscription/library">
          <span className="button__back-button">
            <Icon icon="BackArrow" />Back to Library
          </span>
        </Link>
        <div className="lib__header">
          <div className="lib-cat__title">
            <h2>{activeCategory.get('name')}</h2>
            <InputScaffold>
              <select onChange={this.handleScrollToChange}>{sectionScrollToOptions}</select>
            </InputScaffold>
          </div>
          <div className="lib--search__wrapper">
            <Icon icon="Search" />
            <input
              className="lib--search"
              type="text"
              placeholder="Search"
              onChange={this.handleSearchQueryChange}
              value={this.state.searchQuery}
            />
          </div>
        </div>
        <div className="lib-cat--section__wrapper">{pageContent}</div>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { categoryId } = props.match.params;
  const categories = state.subscription.library.categories;
  const favorites = state.subscription.favorites.favorites;

  const activeCategory =
    categoryId === 'favorites' ? selectFavoritesAsCategory(favorites) : selectActiveCategory(categories, categoryId);

  return {
    activeCategory,
  };
};

export default connect(mapStateToProps)(withRouter(CategoryDetailPage));



// WEBPACK FOOTER //
// ./src/subscription/library/CategoryDetailPage.jsx